import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import {
  getGeneralMarketDetailPageURL,
  getOpenMarketDetailPageURL,
} from '../../../../utils/getter/getProductDetailPageURL.util';
import { ProductPendingAlarmModal } from '../components/ProductPendingAlarmModal';
import { ProductStatusBanner } from '../components/ProductStatusBanner';
import { ProductStatusText } from '../components/ProductStatusText';
import { getGeneralProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { ProductCategoryConstant } from '../../../../constants/product/product.constant';

interface ProductHorizontalListDetailViewProps {
  product: IProduct;
}

export const ProductHorizontalListDetailView = ({ product }: ProductHorizontalListDetailViewProps) => {
  const navigate = useNavigate();

  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const [showProductPendingAlarmModal, setShowProductPendingAlarmModal] = useState<boolean>(false);

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product, isUserLoggedIn });
  }, [product]);

  const productDetailPageURL = useMemo<string>(() => {
    if (product.category === ProductCategoryConstant.OPEN_MARKET) {
      return getOpenMarketDetailPageURL(product.id);
    }
    return getGeneralMarketDetailPageURL(product);
  }, [product.category]);

  return (
    <>
      <Container
        onClick={(e) => {
          e.stopPropagation();

          if (product.status === 'PENDING') {
            setShowProductPendingAlarmModal(true);
          } else if (product.status !== 'SOLDOUT') {
            if (product.category === 'PLUVA') {
              ampli.홈플루바매물모아보기매물클릭({ '매물 코드': product.code });
            } else if (product.category === 'PARTNER') {
              ampli.홈파트너매물모아보기매물클릭({ '매물 코드': product.code });
            } else if (product.category === 'OPEN_MARKET') {
              ampli.홈회원직거래매물모아보기매물클릭({ '매물 코드': product.code });
            }

            navigate(productDetailPageURL);
          }
        }}
      >
        <ImageWrap>
          {product.status === 'PENDING' ? (
            <ProductStatusText text={'판매 예정'} />
          ) : (
            product.status === 'SOLDOUT' && <ProductStatusText text={'판매 완료'} />
          )}

          <Image
            src={
              product.images.filter((image) => image.type === 'LIST').length > 0
                ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                : BlankImage
            }
            alt={
              product.images.filter((image) => image.type === 'LIST').length > 0
                ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                : BlankImage
            }
            isBlurred={product.status === 'PENDING' || product.status === 'SOLDOUT'}
          />

          {(product.status === 'PENDING' || product.status === 'IN_CONTRACT' || product.status === 'SOLDOUT') && (
            <ProductStatusBanner productStatus={product.status} />
          )}
        </ImageWrap>

        <ProductInfo isBlurred={product.status === 'PENDING' || product.status === 'SOLDOUT'}>
          <Title>{product.title}</Title>

          <PriceTitleContainer>
            <Price>{price}</Price>
            {(product.status === 'ONSALE' || product.status === 'IN_CONTRACT') && product.priceVisible === true && (
              <DiscountWrap>
                <Discount>{isUserLoggedIn ? '회원가' : '비회원가'}</Discount>
              </DiscountWrap>
            )}
          </PriceTitleContainer>
        </ProductInfo>
      </Container>

      <ProductPendingAlarmModal
        isShow={showProductPendingAlarmModal}
        onClose={() => setShowProductPendingAlarmModal(false)}
        productTitle={product.title}
        isCaseIH={product.machine?.brand?.name?.includes('케이스')}
      />
    </>
  );
};

const Container = styled.div`
  min-width: 100%;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  gap: 8px;

  z-index: 1;

  &:hover {
    cursor: pointer;
  }
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 268px;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};

  gap: 6px;
`;

const Title = styled.h1`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
`;

const PriceTitleContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: flex-start;
  align-items: center;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;
